import { useQuery } from '@tanstack/react-query'
import { Constants } from 'constants/constants'
import { QueryKeys } from 'constants/query-keys'
import { fetchSafetyLagging } from 'queries/safety/safety'
import { SafetyFetchProps } from 'types/api'
import { IResponse } from 'utils/api'
import { Cell, TableData, applyConditionalFormatting } from 'utils/data'
import { TIME } from 'utils/misc'
import { useAnalytics } from 'utils/analytics'
import { getGreenfieldFilterValues } from 'utils/greenfield'
import { isRegion, isGroup, isDistrict, isCompany } from 'utils/location'

export const getGreenfieldCards = (location: string) => {
    const { SAFETY } = Constants.GREENFIELD_CARDS
    if (isRegion(location)) {
        return {
            parent: SAFETY.LAGGING_REGION,
            children: SAFETY.LAGGING_GROUP,
        }
    }
    if (isGroup(location)) {
        return {
            parent: SAFETY.LAGGING_GROUP,
            children: SAFETY.LAGGING_DISTRICT,
        }
    }
    if (isDistrict(location)) {
        return {
            parent: SAFETY.LAGGING_DISTRICT,
            children: SAFETY.LAGGING_STORE,
        }
    }
    return {
        parent: SAFETY.LAGGING_COMPANY,
        children: SAFETY.LAGGING_REGION,
    }
}

export const getLocationFilter = (location: string) => {
    if (isCompany(location)) return undefined
    if (isRegion(location)) {
        return getGreenfieldFilterValues('REGION', location)
    }
    if (isGroup(location)) {
        return getGreenfieldFilterValues('GROUP', location)
    }
    if (isDistrict(location)) {
        return getGreenfieldFilterValues('DISTRICT', location)
    }
    return undefined
}

const DISPLAY_COLUMNS = new Set(['gi_ytd_rate', 'ti_8wk_rate', 'ti_ytd_rate'])

const isDisplayColumn = (heading: Cell, index: number) =>
    index === 0 || DISPLAY_COLUMNS.has(heading.raw ?? '')

export const stripInternalColumns = (data: TableData): TableData => {
    const headings = data.headings ?? []
    const displayIndexes = headings
        .map((h, i) => (isDisplayColumn(h, i) ? i : -1))
        .filter((i) => i !== -1)

    return {
        headings: displayIndexes.map((i) => headings[i]),
        rows: (data.rows ?? []).map((row) => displayIndexes.map((i) => row[i])),
    }
}

const applyFormattingToRows = (rows: Cell[][]): Cell[][] =>
    rows.map((row) =>
        row.map((cell, index) => {
            if (index === 0 || !cell.color) return cell
            return {
                ...cell,
                ...applyConditionalFormatting(cell.value, cell.color),
            }
        }),
    )

export const mergeLaggingData = (
    parentData: TableData,
    childrenData: TableData,
    location: string,
): TableData => {
    const cleanParent = stripInternalColumns(parentData)
    const cleanChildren = stripInternalColumns(childrenData)
    const headings = cleanChildren.headings ?? cleanParent.headings ?? []
    let parentRow = cleanParent.rows?.[0] ?? []

    const parentHasLocationColumn =
        cleanParent.headings?.length === headings.length
    if (!parentHasLocationColumn && parentRow.length > 0) {
        parentRow = [
            { value: location, color: '', raw: location },
            ...parentRow,
        ]
    }

    const childRows = cleanChildren.rows ?? []
    const allRows = [parentRow, ...childRows]
    return {
        headings,
        rows: applyFormattingToRows(allRows),
    }
}

export function useSafetyLagging({
    env,
    accessToken,
    location,
}: Omit<SafetyFetchProps, 'dynamicSubPath'>): IResponse {
    const { trackEvent } = useAnalytics()
    const { tenMinutes } = TIME
    const { parent, children } = getGreenfieldCards(location)
    const filters = getLocationFilter(location)

    const { data, isLoading, isError, refetch, remove, error } = useQuery(
        [QueryKeys.SAFETY_LAGGING, location],
        async () => {
            const [parentData, childrenData] = await Promise.all([
                fetchSafetyLagging({
                    env,
                    accessToken,
                    dynamicSubPath: parent,
                    filters,
                    trackEvent,
                }),
                fetchSafetyLagging({
                    env,
                    accessToken,
                    dynamicSubPath: children,
                    filters,
                    trackEvent,
                }),
            ])
            return mergeLaggingData(parentData, childrenData, location)
        },
        {
            staleTime: tenMinutes,
            cacheTime: tenMinutes,
            retry: false,
        },
    )

    return { data, isLoading, isError, refetch, remove, error }
}
